import { CheckOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { type FC } from 'react'

import styles from './ehrAppointmentV2.module.scss'
import { Avatar, PROVIDER_TEXT, type PersonOption } from './providerShared'

type ProviderMenuProps = {
  ariaLabel: string
  title: string
  options: PersonOption[]
  people: number[]
  multiselect: boolean
  disabledIds: number[]
  // Shown only when set: a "Primary" tag on it, "Make primary" on the other ticked people.
  primaryId?: number | null
  onToggle: (id: number) => void
  onMakePrimary?: (id: number) => void
  onDone: () => void
}

export const ProviderMenu: FC<ProviderMenuProps> = ({
  ariaLabel,
  title,
  options,
  people,
  multiselect,
  disabledIds,
  primaryId,
  onToggle,
  onMakePrimary,
  onDone,
}) => {
  const hasPrimary = primaryId !== undefined && primaryId !== null
  return (
    <div
      className={styles.providerMenu}
      role='listbox'
      aria-multiselectable={multiselect}
      aria-label={ariaLabel}>
      <div className={styles.providerMenuTitle}>{title}</div>
      {options.map((option) => {
        const isOn = people.includes(option.id)
        return (
          <div key={option.id} className={styles.providerMenuRow}>
            <button
              type='button'
              role='option'
              aria-selected={isOn}
              className={styles.providerMenuOption}
              disabled={disabledIds.includes(option.id)}
              onClick={() => onToggle(option.id)}>
              <span
                className={classNames(styles.providerCheck, { [styles.providerCheckOn]: isOn })}>
                {isOn && <CheckOutlined aria-hidden />}
              </span>
              <Avatar person={option} />
              <span className={styles.providerMenuName}>{option.name}</span>
              <span className={styles.providerMuted}>{option.role}</span>
            </button>
            {isOn && hasPrimary && option.id === primaryId && (
              <span className={classNames(styles.providerPrimary, styles.providerMenuTag)}>
                {PROVIDER_TEXT.primary}
              </span>
            )}
            {isOn && hasPrimary && option.id !== primaryId && (
              <button
                type='button'
                className={classNames(styles.providerLink, styles.providerMenuTag)}
                aria-label={`${PROVIDER_TEXT.makePrimary}: ${option.name}`}
                onClick={() => onMakePrimary?.(option.id)}>
                {PROVIDER_TEXT.makePrimary}
              </button>
            )}
          </div>
        )
      })}
      <div className={styles.providerMenuDivider} />
      <div className={styles.providerMenuFooter}>
        <span className={styles.providerMuted}>{people.length} selected</span>
        <button type='button' className={styles.providerDone} onClick={onDone}>
          {PROVIDER_TEXT.done}
        </button>
      </div>
    </div>
  )
}
